import { Box, Flex } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { MouseEvent, useEffect, useMemo, useState } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { ErrorIndicator } from '@components/ErrorIndicator';
import { FormikPersist } from '@components/FormikPersist';
import { ScrollToError } from '@components/ScrollToError';
import { UpdatingModal } from '@components/UpdatingModal';
import { FeatureFlags } from '@constants/featureFlags';
import { useEnergyContext } from '@features/Energy/Provider';
import { getEnergyTariffByType } from '@features/Energy/utils';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { RemoveDialog } from '@features/RemoveDialog';
import {
  BundleBasketFragment,
  EnergyBasketFragment,
  EnergyFuelType,
  EnergyQuote,
  EnergyQuoteType,
  ProductType,
} from '@generated/gql/graphql';
import { useDeselectEnergy } from '@hooks/useEnergySelection';
import { useEnergyTariffsQuery } from '@hooks/useEnergyTariffsQuery';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { BundleBasketData } from '@src/types/bundle';
import { BooleanSelection } from '@src/types/index';

import { EVBanner } from './EVBanner';
import { EnergyBot } from './EnergyBot';
import { EnergyDetails } from './EnergyDetails';
import { EnergyFAQ } from './EnergyFAQ';
import { PriceUpdateAlert } from './PriceUpdateAlert';
import { SmartMeterBanner } from './SmartMeterBanner';
import { TariffComparison } from './TariffComparison';
import { YourTariff } from './YourTariff';
import { TARIFF_CONTENT_ID } from './constants';
import { useEnergyForm } from './hooks/useEnergyForm';
import './styles.scss';
import { EnergyFormValues, EnergyTariffFormConfig } from './types';
import { hasPrepaymentMeter } from './utils';
import { validateForm } from './validateForm';

/**
 * Journey-agnostic energy tariff form.
 */
const EnergyTariffFormBase = ({
  onSubmitSuccess,
  onEditDetails,
  persistKey,
  evBanner,
  remove,
  renderNavBar,
  enableFormSync = false,
  smartMeterBanner,
  faq,
}: EnergyTariffFormConfig) => {
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const { value: isEVEnabled } = useFeatureFlag(FeatureFlags.EV_ENABLED, false);

  const {
    basket: basketCast,
    basketRaw: basketRawCast,
    isDetailsLoading,
  } = useEnergyContext();
  const { loading: isEnergyTariffsLoading, tariffs } = useEnergyTariffsQuery({
    // skip fetching tariffs if details mutation is loading
    // otherwise we fetch the previous tariffs again with
    // old data
    skip: isDetailsLoading,
  });

  const {
    deselectQuotes,
    loading: deselectLoading,
    error: deselectError,
  } = useDeselectEnergy();
  // Basket data availability is checked on Energy provider level
  const basket = basketCast as BundleBasketData;
  const basketRaw = basketRawCast as BundleBasketFragment;

  const { formRef, initialValues, submitForm, isSubmitting, hasSubmitError } =
    useEnergyForm();

  useEffect(() => {
    updatingDialog.setOpen(deselectLoading || isSubmitting);
  }, [deselectLoading, isSubmitting, updatingDialog]);

  const evInterested = basketRaw?.energy?.details
    ?.electricVehicleTariffExpressionOfInterest
    ? BooleanSelection.Yes
    : BooleanSelection.No;
  const committedQuoteType = basketRaw?.energy?.selectedQuote?.quoteType;

  const committedEnergyValues = useMemo(
    () =>
      committedQuoteType
        ? { evInterested, tariffType: committedQuoteType }
        : undefined,
    [evInterested, committedQuoteType]
  );

  if (deselectError || hasSubmitError) {
    return <ErrorIndicator />;
  }

  const { willCreditCheck, hasSmartmeter } = basket;

  // TODO: should we redirect to /bundle at this point, since they arrived on
  // the energy page without any energy provided in the basket?
  if (!basketRaw?.energy || !basket?.energy) return null;

  const energyFixedTariff = getEnergyTariffByType(
    tariffs,
    EnergyQuoteType.Fixed
  );
  const energyTrackerTariff = getEnergyTariffByType(
    tariffs,
    EnergyQuoteType.Tracker
  );
  const energyVariableTariff = getEnergyTariffByType(
    tariffs,
    EnergyQuoteType.Variable
  );

  const energy = basketRaw.energy as EnergyBasketFragment;
  const { details, fuelType, selectedQuote } = energy;

  const showEVBanner =
    isEVEnabled &&
    !hasPrepaymentMeter(details) &&
    // Waits until details request settled
    !isDetailsLoading &&
    fuelType !== EnergyFuelType.GasOnly;

  const handleSubmitSucceeded = (basket: Nullable<BundleBasketFragment>) => {
    if (!basket?.energy?.selectedQuote) {
      return;
    }

    onSubmitSuccess(basket);
  };

  const availableTariffTypes = [
    EnergyQuoteType.Variable,
    ...(energyFixedTariff ? [EnergyQuoteType.Fixed] : []),
    ...(energyTrackerTariff ? [EnergyQuoteType.Tracker] : []),
  ];

  const handleSubmit = (formValues: EnergyFormValues) =>
    submitForm(formValues, showEVBanner, energy, handleSubmitSucceeded);

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    formRef?.current?.requestSubmit();
    return;
  };

  const handleRemove = async () => {
    if (!remove) return;

    await deselectQuotes(basket.id);
    remove.onSuccess();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={(values) => validateForm(values, availableTariffTypes)}
    >
      {({ handleSubmit }) => (
        <>
          {enableFormSync && (
            <FormSync committedValues={committedEnergyValues} />
          )}
          <FormikPersist
            name={persistKey}
            committedValues={committedEnergyValues}
            suspend={isSubmitting}
          />
          <ScrollToError orderedFields={[TARIFF_CONTENT_ID]} />
          <Box paddingBottom="600">
            <form ref={formRef} onSubmit={handleSubmit}>
              <Flex direction="column" className="wrapper" spacing="2xl">
                <EnergyBot energy={basket.energy} showEVBanner={showEVBanner} />
                <YourTariff
                  variable={energyVariableTariff as EnergyQuote}
                  tracker={energyTrackerTariff}
                  fixed={energyFixedTariff}
                  isLoading={isDetailsLoading || isEnergyTariffsLoading}
                  fuelType={fuelType}
                  hasSmartmeter={hasSmartmeter}
                />
                <PriceUpdateAlert />
                <Flex direction="column" spacing="2xl">
                  <EnergyDetails
                    details={details}
                    fuelType={fuelType}
                    isLoading={isDetailsLoading}
                    onEdit={onEditDetails}
                  />
                  {!hasPrepaymentMeter(details) && !isEnergyTariffsLoading && (
                    <TariffComparison
                      variable={energyVariableTariff as EnergyQuote}
                      tracker={energyTrackerTariff ?? null}
                      fixed={energyFixedTariff ?? null}
                      fuelType={fuelType}
                      isLoading={isDetailsLoading}
                    />
                  )}
                  {showEVBanner && (
                    <EVBanner
                      onLinkClick={evBanner.onLinkClick}
                      onBannerDisplayed={evBanner.onBannerDisplayed}
                    />
                  )}
                  {!hasSmartmeter && (
                    <SmartMeterBanner
                      onBannerDisplayed={smartMeterBanner.onBannerDisplayed}
                    />
                  )}
                </Flex>
              </Flex>
            </form>
          </Box>
          <EnergyFAQ
            willCreditCheck={willCreditCheck}
            onFaqLinkClicked={faq.onFaqLinkClicked}
            onFaqHeaderDisplayed={faq.onFaqHeaderDisplayed}
            excludeTopics={faq.excludeTopics}
          />
          <UpdatingModal
            heading="Updating your energy tariff..."
            productType={ProductType.Energy}
            isRemoving={deselectLoading}
          />
          {remove && (
            <RemoveDialog
              isOpen={removeDialogOpen}
              onClose={() => setRemoveDialogOpen(false)}
              onRemove={handleRemove}
              productType={ProductType.Energy}
            />
          )}
          {renderNavBar({
            onSave: handleSave,
            onRemoveClick: remove ? () => setRemoveDialogOpen(true) : undefined,
            isSubmitting,
            hasSelectedQuote: !!selectedQuote,
          })}
        </>
      )}
    </Formik>
  );
};

export { EnergyTariffFormBase };
